// Audit log: every change, who made it, when, from where, and exactly what changed. Updates live.
import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api';
import { useAuth } from '../auth';
import { useLiveTopics } from '../live';
import { formatDateTime } from '../ui';
import type { AuditEvent } from '../types';
import { Page, useCrumbs } from './Shell';

const ACTION_GROUPS = [
  ['', 'All activity'], ['auth.', 'Sign-ins and security'], ['user.', 'People and invitations'], ['client.', 'Clients'],
] as const;

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '–' : String(v));

export function AuditLog() {
  const { me } = useAuth();
  const setCrumbs = useCrumbs();
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useLiveTopics(me?.is_staff ? 'workspace' : me?.client ? `client:${me.client.id}` : null);
  useEffect(() => setCrumbs([{ label: 'Audit log' }]), [setCrumbs]);
  useEffect(() => { const t = window.setTimeout(() => setQuery(q), 300); return () => window.clearTimeout(t); }, [q]);

  const feed = useInfiniteQuery({
    queryKey: ['audit', action, query],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: '100' });
      if (pageParam) p.set('before_id', String(pageParam));
      if (action) p.set('action', action);
      if (query) p.set('q', query);
      return get<{ events: AuditEvent[]; next_before_id: number | null }>(`/api/audit?${p}`);
    },
    getNextPageParam: (last) => last.next_before_id ?? undefined,
  });
  const events = feed.data?.pages.flatMap((p) => p.events) ?? [];

  return (
    <Page title="Audit log" eyebrow={me?.is_staff ? 'Admin' : me?.client?.name}>
      <div className="row wrap" style={{ marginBottom: 14 }}>
        <select className="input" style={{ width: 'auto' }} value={action} onChange={(e) => setAction(e.target.value)} aria-label="Filter by type">
          {ACTION_GROUPS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <input className="input" style={{ maxWidth: 320 }} placeholder="Search person, action or detail" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the audit log" />
      </div>
      <div className="card table-wrap">
        <table className="table">
          <thead><tr><th style={{ width: 170 }}>When</th><th>Who</th><th>What</th><th>Details</th><th>From</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="muted small">{formatDateTime(e.at)}</td>
                <td className="small">{e.actor}</td>
                <td><span className={`badge ${e.action.includes('failed') || e.action.includes('blocked') ? 'bad' : e.action.startsWith('auth.') ? 'info' : ''}`}>{e.action}</span></td>
                <td className="small">
                  {e.summary}
                  {e.changes && (
                    <div className="changes">
                      {Object.entries(e.changes).map(([field, [before, after]]) => (
                        <span key={field} style={{ display: 'contents' }}><span className="muted">{field}</span><span><del>{show(before)}</del> → <ins>{show(after)}</ins></span></span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="muted tiny mono">{e.ip ?? '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!feed.isLoading && !events.length && <div className="empty">Nothing recorded yet.</div>}
      </div>
      {feed.hasNextPage && <div className="row" style={{ justifyContent: 'center', marginTop: 14 }}><button className="btn" onClick={() => feed.fetchNextPage()} disabled={feed.isFetchingNextPage}>Load older</button></div>}
    </Page>
  );
}
